import { useState, useEffect } from 'react';
import { MapPin, Star, Users, Heart, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import LazyImage from './LazyImage';

const ClubCard = ({ club }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkFavorite = async () => {
      if (!user?.id || !club?.id) return;
      try {
        const res = await api.get(`/favorites/check/club/${club.id}?user_id=${user.id}`);
        setIsFavorite(res.data.isFavorite);
      } catch (error) {
        // Silent fail - not critical
      }
    };
    checkFavorite();
  }, [user, club?.id]);

  const handleToggleFavorite = async (e) => {
    e.stopPropagation(); // Prevent navigation
    if (!user?.id) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    try {
      setLoading(true);
      const res = await api.post(`/favorites/toggle/club/${club.id}?user_id=${user.id}`);
      setIsFavorite(res.data.isFavorite);
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={() => navigate(`/clubs/${club.id}`)}
      data-testid={`club-card-${club.id}`}
      className="theme-bg-card border theme-border rounded-xl overflow-hidden hover:border-[#00d4ff]/50 hover:-translate-y-1 transition-all duration-200 cursor-pointer relative"
    >
      {/* Favorite Button */}
      <button
        onClick={handleToggleFavorite}
        disabled={loading}
        className={`absolute top-3 right-3 z-10 p-2 rounded-full backdrop-blur-sm transition-all duration-200 ${
          isFavorite 
            ? 'bg-red-500/90 text-white' 
            : 'bg-black/40 text-white hover:bg-red-500/70'
        }`}
        data-testid={`favorite-btn-${club.id}`}
      >
        {loading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
        )}
      </button>

      <div className="aspect-video w-full overflow-hidden">
        {club.imageUrl ? (
          <LazyImage
            src={club.imageUrl}
            alt={club.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-800">
            <MapPin size={48} className="text-slate-600" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-bold theme-text">{club.name}</h3>
          <div className="flex items-center gap-1">
            <Star size={16} className="text-[#c8e600] fill-current" />
            <span className="text-sm font-mono text-zinc-300">
              {club.rating}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm theme-text-secondary mb-3">
          <MapPin size={16} />
          <span>{club.city}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm text-zinc-300">
            <Users size={16} className="theme-accent" />
            <span>{club.courtsCount} terrains</span>
          </div>
          <span className="text-sm font-mono text-[#c8e600]">
            {club.pricePerHour}€/h
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClubCard;
